import {PaymentMethod} from '@sollapay/enums';
import {Button, Illustration, InfoRow, Modal, Typography} from '@sollapay/ui/components';
import {SaveMoneyIllustration} from '@sollapay/ui/illustrations';
import {formatNIS, parseMoney} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import type {FC} from 'react';

export interface PaymentSentModalProps {
  isOpen: boolean;
  buyerName: string;
  assetLabel: string;
  purchasePriceNis: string;
  initiationMethod: PaymentMethod;
  amount: string;
  onClose: () => void;
}

export const PaymentSentModal: FC<PaymentSentModalProps> = ({
  isOpen,
  buyerName,
  assetLabel,
  purchasePriceNis,
  initiationMethod,
  amount,
  onClose
}) => {
  const {t} = useTranslation('trustAccounts');
  const prefix = 'view.buyers.paymentSent';
  const methodPrefix = 'view.buyers.paymentRequest.methods';

  const isPis = initiationMethod === PaymentMethod.PIS;

  const methodLabel = isPis ? t(`${methodPrefix}.pis`) : t(`${methodPrefix}.manual_transfer`);

  const description = isPis ? t(`${prefix}.descriptionPis`) : t(`${prefix}.descriptionManual`);

  return (
    <Modal
      size="sm"
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      title={t(`${prefix}.title`, {buyerName})}
      description={description}
      illustration={
        <Illustration width={91} height={71}>
          <SaveMoneyIllustration />
        </Illustration>
      }
      showCloseButton={false}
      outsidePress="close"
      footer={
        <div className="-mx-6 flex flex-1 flex-col border-t border-border">
          <div className="flex items-center gap-3 px-6 pt-4">
            <Button variant="primary" size="stretch" onClick={onClose}>
              {t(`${prefix}.cta`)}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3 rounded-md border border-border-subtle bg-background-secondary p-4 py-3">
        <Typography size="sm" weight="semibold" className="text-start">
          {t(`${prefix}.summaryTitle`)}
        </Typography>
        <div className="flex flex-col gap-3">
          <InfoRow label={t(`${prefix}.buyer`)} value={buyerName} />
          <InfoRow label={t(`${prefix}.unit`)} value={assetLabel} />
          <InfoRow
            label={t(`${prefix}.purchasePrice`)}
            value={formatNIS(parseMoney(purchasePriceNis))}
          />
          <InfoRow label={t(`${prefix}.paymentMethod`)} value={methodLabel} />
          <InfoRow label={t(`${prefix}.requestAmount`)} value={formatNIS(parseMoney(amount))} />
        </div>
      </div>
    </Modal>
  );
};
